import { periodsDisjoint, type WorkPeriod, type WorkSchedule, type WorkWeek } from '@erp/shared';
import { CalendarRange, Copy, Pencil, Plus, Star, Trash2, X } from 'lucide-react';
import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';

import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle,
} from '../../../components/shadcn/index.js';
import { Badge, Button, Card, confirmDialog, EmptyState, FormField, Input, Skeleton } from '../../../components/ui/index.js';
import {
  attendanceErrorCode,
  createWorkSchedule,
  deleteWorkSchedule,
  listWorkSchedules,
  updateWorkSchedule,
} from '../../../lib/attendance.js';

/** Which weekday a week starts on, per language: Saturday where the shops are, Monday in German, Sunday in English. */
function weekOrder(lang: string): number[] {
  const start = lang.startsWith('de') ? 1 : lang.startsWith('en') ? 0 : 6;
  return Array.from({ length: 7 }, (_, i) => (start + i) % 7);
}

const SHOP_DAY: WorkPeriod[] = [{ start: '09:00', end: '17:00' }];
/** The starting point a new schedule gets: Saturday–Thursday 09:00–17:00, Friday off. */
const NEW_WEEK: WorkWeek = [SHOP_DAY, SHOP_DAY, SHOP_DAY, SHOP_DAY, SHOP_DAY, [], SHOP_DAY];

interface Draft {
  id: string | null;
  name: string;
  periods: WorkWeek;
  graceMinutes: string;
  minOvertimeMinutes: string;
  isDefault: boolean;
  isActive: boolean;
}

function draftOf(s: WorkSchedule | null): Draft {
  return s
    ? {
        id: s.id, name: s.name, periods: s.periods.map((d) => d.map((p) => ({ ...p }))),
        graceMinutes: String(s.graceMinutes), minOvertimeMinutes: String(s.minOvertimeMinutes),
        isDefault: s.isDefault, isActive: s.isActive,
      }
    : { id: null, name: '', periods: NEW_WEEK.map((d) => d.map((p) => ({ ...p }))), graceMinutes: '10', minOvertimeMinutes: '30', isDefault: false, isActive: true };
}

/** `HH:MM` plus minutes, wrapping at midnight. */
function clockPlus(clock: string, minutes: number): string {
  const [h, m] = clock.split(':').map(Number) as [number, number];
  const total = (((h * 60 + m + minutes) % 1440) + 1440) % 1440;
  return `${String(Math.floor(total / 60)).padStart(2, '0')}:${String(total % 60).padStart(2, '0')}`;
}

/**
 * A day turned into a split shift. A morning start becomes the shape shops here
 * actually keep — morning until 13:00, back 16:00–21:00; anything else gains a
 * second period starting an hour after the first ends. Never an overlap.
 */
function splitOf(p: WorkPeriod): WorkPeriod[] {
  if (p.start < '13:00' && p.end > p.start) return [{ start: p.start, end: '13:00' }, { start: '16:00', end: '21:00' }];
  return [p, { start: clockPlus(p.end, 60), end: clockPlus(p.end, 240) }];
}

/** "09:00–13:00, 16:00–21:00" for one day; an end before its start reads as running past midnight. */
function dayText(periods: WorkPeriod[]): string {
  return periods.map((p) => `${p.start}–${p.end}`).join(', ');
}

/**
 * Work schedules: what a working day looks like, per weekday. Split shifts are
 * two periods on a day; a period whose end is before its start runs past
 * midnight. One schedule is the default — everyone without their own falls back
 * to it.
 */
export function WorkSchedulesPanel(): JSX.Element {
  const { t, i18n } = useTranslation('hr');
  const [items, setItems] = useState<WorkSchedule[] | null>(null);
  const [draft, setDraft] = useState<Draft | null>(null);
  const [saving, setSaving] = useState(false);
  const order = weekOrder(i18n.language);

  const reload = useCallback(async (): Promise<void> => {
    try { setItems(await listWorkSchedules()); } catch { setItems([]); }
  }, []);
  useEffect(() => { void reload(); }, [reload]);

  const fail = (err: unknown): void => {
    const code = attendanceErrorCode(err);
    toast.error(t(`attendance.errors.${code ?? 'generic'}`, { defaultValue: t('attendance.errors.generic') }));
  };

  const save = async (): Promise<void> => {
    if (!draft || !draft.name.trim()) return;
    const clash = order.find((d) => !periodsDisjoint(draft.periods[d] ?? []));
    if (clash !== undefined) {
      toast.error(t('attendance.schedules.overlap', { day: t(`attendance.weekdays.${clash}`) }));
      return;
    }
    setSaving(true);
    try {
      const body = {
        name: draft.name.trim(),
        periods: draft.periods,
        graceMinutes: Math.max(0, Math.min(240, Number(draft.graceMinutes) || 0)),
        minOvertimeMinutes: Math.max(0, Math.min(720, Number(draft.minOvertimeMinutes) || 0)),
        isDefault: draft.isDefault,
        isActive: draft.isActive,
      };
      if (draft.id) await updateWorkSchedule(draft.id, body);
      else await createWorkSchedule(body);
      toast.success(t('attendance.sessions.saved'));
      setDraft(null);
      await reload();
    } catch (err) {
      fail(err);
    } finally {
      setSaving(false);
    }
  };

  const remove = async (s: WorkSchedule): Promise<void> => {
    if (!(await confirmDialog({ message: t('attendance.schedules.confirmDelete', { name: s.name }), tone: 'danger' }))) return;
    try {
      await deleteWorkSchedule(s.id);
      await reload();
    } catch (err) {
      const code = attendanceErrorCode(err);
      if (code === 'schedule_in_use') toast.error(t('attendance.errors.schedule_in_use', { n: s.employeeCount }));
      else fail(err);
    }
  };

  const makeDefault = async (s: WorkSchedule): Promise<void> => {
    try {
      await updateWorkSchedule(s.id, { isDefault: true });
      await reload();
    } catch (err) {
      fail(err);
    }
  };

  const setDay = (weekday: number, periods: WorkPeriod[]): void => {
    // Functional, so two edits in one tick both land instead of the second
    // overwriting the first from a stale draft.
    setDraft((cur) => {
      if (!cur) return cur;
      const next = cur.periods.map((d) => d.map((p) => ({ ...p })));
      next[weekday] = periods;
      return { ...cur, periods: next };
    });
  };

  /** Copy the first working day's hours onto every other working day. */
  const copyFirst = (): void => {
    if (!draft) return;
    const first = order.find((d) => (draft.periods[d] ?? []).length > 0);
    if (first === undefined) return;
    const src = draft.periods[first]!;
    setDraft({
      ...draft,
      periods: draft.periods.map((d) => (d.length > 0 ? src.map((p) => ({ ...p })) : d)),
    });
  };

  if (!items) return <Card className="p-4"><Skeleton className="h-32 w-full" /></Card>;

  return (
    <div className="flex flex-col gap-4">
      <div className="flex items-start justify-between gap-4">
        <p className="max-w-3xl text-sm text-fg-muted">{t('attendance.schedules.description')}</p>
        <Button onClick={() => setDraft(draftOf(null))} className="gap-1.5">
          <Plus className="h-4 w-4" aria-hidden />
          {t('attendance.schedules.add')}
        </Button>
      </div>

      {items.length === 0 ? (
        <EmptyState
          icon={<CalendarRange className="h-8 w-8" aria-hidden />}
          title={t('attendance.schedules.empty')}
          description={t('attendance.schedules.emptyDescription')}
        />
      ) : (
        <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
          {items.map((s) => (
            <Card key={s.id} className={`p-4 ${s.isActive ? '' : 'opacity-60'}`}>
              <div className="flex items-start gap-2">
                <div className="min-w-0 flex-1">
                  <div className="flex flex-wrap items-center gap-2">
                    <h3 className="font-semibold">{s.name}</h3>
                    {s.isDefault && <Badge tone="brand">{t('attendance.schedules.default')}</Badge>}
                    {!s.isActive && <Badge>{t('attendance.schedules.inactive')}</Badge>}
                  </div>
                  <p className="mt-0.5 text-xs text-fg-muted">
                    {t('attendance.schedules.meta', { grace: s.graceMinutes, overtime: s.minOvertimeMinutes, assigned: s.employeeCount })}
                  </p>
                </div>
                {!s.isDefault && (
                  <Button variant="ghost" size="sm" onClick={() => void makeDefault(s)} title={t('attendance.schedules.makeDefault')} aria-label={t('attendance.schedules.makeDefault')}>
                    <Star className="h-4 w-4" aria-hidden />
                  </Button>
                )}
                <Button variant="ghost" size="sm" onClick={() => setDraft(draftOf(s))} title={t('attendance.sessions.edit')} aria-label={t('attendance.sessions.edit')}>
                  <Pencil className="h-4 w-4" aria-hidden />
                </Button>
                <Button variant="ghost" size="sm" onClick={() => void remove(s)} title={t('attendance.sessions.delete')} aria-label={t('attendance.sessions.delete')}>
                  <Trash2 className="h-4 w-4" aria-hidden />
                </Button>
              </div>
              <dl className="mt-3 grid grid-cols-[auto_1fr] gap-x-4 gap-y-1 text-sm">
                {order.map((d) => (
                  <div key={d} className="contents">
                    <dt className="text-fg-muted">{t(`attendance.weekdays.${d}`)}</dt>
                    <dd className="font-mono tabular-nums" dir="ltr">
                      {(s.periods[d] ?? []).length > 0
                        ? <span className="block text-end rtl:text-start">{dayText(s.periods[d]!)}</span>
                        : <span className="block text-end font-sans text-fg-subtle rtl:text-start">{t('attendance.status.off')}</span>}
                    </dd>
                  </div>
                ))}
              </dl>
            </Card>
          ))}
        </div>
      )}

      <Dialog open={draft !== null} onOpenChange={(o) => { if (!o) setDraft(null); }}>
        {draft && (
          <DialogContent className="max-w-2xl">
            <DialogHeader>
              <DialogTitle>{draft.id ? t('attendance.schedules.editTitle') : t('attendance.schedules.add')}</DialogTitle>
            </DialogHeader>
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
              <div className="sm:col-span-3">
                <FormField id="ws-name" label={t('attendance.schedules.name')} required>
                  <Input id="ws-name" value={draft.name} maxLength={120} onChange={(e) => setDraft({ ...draft, name: e.target.value })} autoFocus />
                </FormField>
              </div>
              <FormField id="ws-grace" label={t('attendance.schedules.grace')} helperText={t('attendance.schedules.graceHint')}>
                <Input id="ws-grace" type="number" min={0} max={240} value={draft.graceMinutes} onChange={(e) => setDraft({ ...draft, graceMinutes: e.target.value })} />
              </FormField>
              <FormField id="ws-ot" label={t('attendance.schedules.minOvertime')} helperText={t('attendance.schedules.minOvertimeHint')}>
                <Input id="ws-ot" type="number" min={0} max={720} value={draft.minOvertimeMinutes} onChange={(e) => setDraft({ ...draft, minOvertimeMinutes: e.target.value })} />
              </FormField>
              <div className="flex flex-col justify-center gap-2 text-sm">
                <label className="flex items-center gap-2">
                  <input type="checkbox" className="h-4 w-4" checked={draft.isDefault} onChange={(e) => setDraft({ ...draft, isDefault: e.target.checked })} />
                  {t('attendance.schedules.isDefault')}
                </label>
                <label className="flex items-center gap-2">
                  <input type="checkbox" className="h-4 w-4" checked={draft.isActive} onChange={(e) => setDraft({ ...draft, isActive: e.target.checked })} />
                  {t('attendance.schedules.isActive')}
                </label>
              </div>
            </div>

            <div className="flex items-center justify-between">
              <h4 className="text-sm font-semibold">{t('attendance.schedules.week')}</h4>
              <Button size="sm" variant="ghost" onClick={copyFirst} className="gap-1.5">
                <Copy className="h-4 w-4" aria-hidden />
                {t('attendance.schedules.copyFirst')}
              </Button>
            </div>
            <div className="flex flex-col gap-2">
              {order.map((d) => {
                const periods = draft.periods[d] ?? [];
                const working = periods.length > 0;
                return (
                  <div key={d} className="flex flex-wrap items-center gap-2 rounded-lg border border-border px-3 py-2">
                    <label className="flex w-32 items-center gap-2 text-sm font-medium">
                      <input
                        type="checkbox"
                        className="h-4 w-4"
                        checked={working}
                        onChange={(e) => setDay(d, e.target.checked ? [{ start: '09:00', end: '17:00' }] : [])}
                      />
                      {t(`attendance.weekdays.${d}`)}
                    </label>
                    {!working && <span className="text-sm text-fg-subtle">{t('attendance.status.off')}</span>}
                    {!periodsDisjoint(periods) && (
                      <span className="w-full text-xs text-danger">{t('attendance.schedules.overlap', { day: t(`attendance.weekdays.${d}`) })}</span>
                    )}
                    {periods.map((p, i) => (
                      <span key={i} className="inline-flex items-center gap-1" dir="ltr">
                        <input
                          type="time"
                          value={p.start}
                          aria-label={`${t(`attendance.weekdays.${d}`)} ${t('attendance.schedules.from')}`}
                          onChange={(e) => setDay(d, periods.map((q, j) => (j === i ? { ...q, start: e.target.value } : q)))}
                          className="h-8 rounded-md border border-border bg-bg px-2 text-sm"
                        />
                        <span className="text-fg-muted">–</span>
                        <input
                          type="time"
                          value={p.end}
                          aria-label={`${t(`attendance.weekdays.${d}`)} ${t('attendance.schedules.to')}`}
                          onChange={(e) => setDay(d, periods.map((q, j) => (j === i ? { ...q, end: e.target.value } : q)))}
                          className="h-8 rounded-md border border-border bg-bg px-2 text-sm"
                        />
                        {p.end <= p.start && p.end !== '' && (
                          <span className="text-xs text-warning" title={t('attendance.schedules.overnightHint')}>+1</span>
                        )}
                        {periods.length > 1 && (
                          <button
                            type="button"
                            className="rounded p-0.5 text-fg-muted hover:text-danger"
                            onClick={() => setDay(d, periods.filter((_, j) => j !== i))}
                            aria-label={t('attendance.schedules.removePeriod')}
                          >
                            <X className="h-3.5 w-3.5" aria-hidden />
                          </button>
                        )}
                      </span>
                    ))}
                    {working && periods.length < 2 && (
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => setDay(d, splitOf(periods[0]!))}
                        className="gap-1 text-xs"
                      >
                        <Plus className="h-3.5 w-3.5" aria-hidden />
                        {t('attendance.schedules.addSplit')}
                      </Button>
                    )}
                  </div>
                );
              })}
            </div>
            <p className="text-xs text-fg-muted">{t('attendance.schedules.overnightHint')}</p>
            <DialogFooter>
              <Button variant="secondary" onClick={() => setDraft(null)} disabled={saving}>{t('attendance.sessions.cancel')}</Button>
              <Button onClick={() => void save()} loading={saving} disabled={!draft.name.trim()}>{t('attendance.sessions.save')}</Button>
            </DialogFooter>
          </DialogContent>
        )}
      </Dialog>
    </div>
  );
}
